import { useState, useMemo } from "react";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../constants/pagination";

// Generic pagination hook interface
export interface UsePaginationReturn<T> {
  currentPage: number;
  itemsPerPage: number;
  paginatedItems: T[];
  totalPages: number;
  handlePageChange: (pageNumber: number) => void;
  handleItemsPerPageChange: (newSize: string | number) => void;
}

/**
 * Global pagination hook for consistent pagination across the application
 * @param items - Array of items to paginate
 * @param initialPage - Initial page number (default from constants)
 * @param initialPageSize - Initial page size (default from constants)
 * @returns Pagination state and handlers
 */
export const usePagination = <T>(
  items: T[],
  initialPage: number = DEFAULT_PAGE,
  initialPageSize: number = DEFAULT_PAGE_SIZE
): UsePaginationReturn<T> => {
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState<number>(initialPageSize);

  // Ensure items is always an array
  const safeItems = useMemo(() => Array.isArray(items) ? items : [], [items]);

  const paginatedItems = useMemo<T[]>(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return safeItems.slice(indexOfFirstItem, indexOfLastItem);
  }, [safeItems, currentPage, itemsPerPage]);

  const totalPages = useMemo<number>(() => {
    return Math.ceil(safeItems.length / itemsPerPage);
  }, [safeItems.length, itemsPerPage]);

  const handlePageChange = (pageNumber: number): void => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleItemsPerPageChange = (newSize: string | number): void => {
    setItemsPerPage(parseInt(newSize.toString()));
    setCurrentPage(1);
  };

  return {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  };
};
